import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Validate that an image `src` uses a safe scheme before rendering.
 * Blocks `javascript:`, `vbscript:`, `file:`, and `data:text/html` payloads
 * flagged by Veracode as CWE-80 (Basic XSS).
 *
 * @param src - Candidate URL string (typically from API or user input)
 * @returns true when the URL is safe to use as an `<img src>` attribute
 */
export function isSafeImageSrc(src: string): boolean {
  if (!src) return false;

  // Allow same-origin relative paths (defensive; current callers use absolute URLs)
  if (src.startsWith("/") && !src.startsWith("//")) return true;

  let url: URL;
  try {
    url = new URL(src, window.location.origin);
  } catch {
    return false;
  }

  const protocol = url.protocol.toLowerCase();

  if (protocol === "https:" || protocol === "http:" || protocol === "blob:") {
    return true;
  }

  if (protocol === "data:") {
    // Only allow image MIME types; block data:text/html and other payloads
    return /^data:image\/(png|jpe?g|gif|webp|svg\+xml);/i.test(src);
  }

  return false;
}

export interface SafeImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> {
  src: string | null | undefined;
  alt: string;
  fallbackClassName?: string;
}

/**
 * Drop-in replacement for `<img>` that validates the `src` URL scheme
 * before rendering. Falls back to a neutral placeholder element when
 * the URL is missing or uses a disallowed scheme.
 *
 * Fixes Veracode findings #10 and #11 (pwc-marketplace admin).
 */
export const SafeImage = React.forwardRef<HTMLImageElement, SafeImageProps>(
  ({ src, alt, className, fallbackClassName, ...rest }, ref) => {
    if (!isSafeImageSrc(src ?? "")) {
      return (
        <div
          role="img"
          aria-label={alt}
          className={cn("bg-bg-fill-neutral-subtle", fallbackClassName ?? className)}
        />
      );
    }

    return (
      <img
        ref={ref}
        src={src ?? undefined}
        alt={alt}
        className={className}
        {...rest}
      />
    );
  },
);

SafeImage.displayName = "SafeImage";

export default SafeImage;